import React, { useState } from "react";
import { Modal } from "../modal/Modal";
import { HelpCircleIcon, Copy, Check, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card } from "../utils/Card";
import { HistoryButton, type HistoryTarget } from "../history/HistoryButton";

interface FormLayoutProps {
  title: string;
  children: React.ReactNode;
  help?:
    | Record<string, string>
    | {
        text: string;
        image: string;
      };
  onCopy?: () => void;
  onDownload?: () => void;
  /** Enregistrement dont l'historique des versions est consultable. */
  history?: HistoryTarget;
}

const FormLayout = ({
  title,
  children,
  help,
  onCopy,
  onDownload,
  history,
}: FormLayoutProps) => {
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const handleCopy = async () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onCopy?.();
  };

  return (
    <>
      <Card
        title={title}
        actions={
          <div className="flex gap-2 items-center">
            {!!onCopy && !copied ? (
              <button
                type="button"
                onClick={() => void handleCopy()}
                title={t("common.copyMarkdown")}
                aria-label={t("common.copyMarkdown")}
                className="me-3 cursor-pointer text-gray-300 transition-colors hover:text-blue-600 dark:text-gray-600 dark:hover:text-blue-400"
              >
                <Copy className="h-4 w-4" />
              </button>
            ) : onCopy ? (
              <Check
                className="me-3 h-4 w-4 text-green-500"
                aria-label={t("common.copyMarkdownDone")}
              />
            ) : null}
            {!!onDownload && (
              <button
                type="button"
                onClick={() => void onDownload()}
                title={t("common.downloadMarkdown")}
                aria-label={t("common.downloadMarkdown")}
                className="me-3 cursor-pointer text-gray-300 transition-colors hover:text-blue-600 dark:text-gray-600 dark:hover:text-blue-400"
              >
                <Download className="h-4 w-4" />
              </button>
            )}
            {!!history && (
              <span className="me-3">
                <HistoryButton {...history} />
              </span>
            )}
            {help && (
              <HelpCircleIcon
                className="w-6 h-6 cursor-pointer text-gray-300 dark:text-gray-600"
                onClick={() => setIsHelpModalOpen(true)}
              />
            )}
          </div>
        }
      >
        <div className="max-w-3xl mx-auto">{children}</div>
      </Card>
      {help && (
        <Modal
          isOpen={isHelpModalOpen}
          closeIcon={true}
          onClose={() => setIsHelpModalOpen(false)}
          title={t("form.help")}
        >
          <p className="mb-4 text-gray-700 dark:text-gray-300 max-w-[380px]">
            {t(help.text)}
          </p>
          <div className="flex justify-center">
            <img src={help.image} />
          </div>
        </Modal>
      )}
    </>
  );
};

export default FormLayout;
